import React from 'react';
import { CapabilityNoetivraNote } from './CapabilityNoetivraNote';
import { motion } from 'framer-motion';
import { Heart, Target, CheckCircle, XCircle } from 'lucide-react';
export function EmotionalRegulationPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-198" data-mp="319jm10oq3b6s">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-199" data-mp="368nshjk9iutf">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-200" data-mp="2s9eodi9bzr5t">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-201" data-mp="2nabhds9sjav7">
            <span className="w-8 h-px bg-teal" data-id="element-202" data-mp="2ncnyy4bmzj7q"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-203" data-mp="gju4uh0jrdax">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-204" data-mp="lif8yrix6h4n"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-205" data-mp="1av2my4p483tb">
            Emotional Regulation
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-206" data-mp="1jo67ewy6hgxg">
            At SyNeural, emotional regulation refers to the ability to remain
            stable enough under stress, frustration, pressure, or conflict to
            think clearly and act responsibly. It does not mean suppressing
            emotion or becoming detached. It means being able to experience
            emotion without being ruled by it, and to recover from intensity
            without allowing it to distort judgment, relationships, or
            performance.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-207" data-mp="2cbs91znuibho">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-208" data-mp="27eid015yb1kv">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-209" data-mp="1e10q7iw4m052">
            Emotional regulation matters because reasoning, planning,
            cooperation, and leadership all deteriorate when people become
            excessively reactive, impulsive, or destabilized. Even strong
            intelligence or technical competence can be undermined when a person
            cannot maintain perspective under pressure. Emotional regulation is
            therefore one of the core foundations of readiness across personal,
            civic, institutional, and leadership settings.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-210" data-mp="7l6xn51uw8u2">
        <div className="max-w-4xl mx-auto" data-id="element-211" data-mp="7iu8o7xoky7i">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-212" data-mp="mlnfbn58fh">
            <div className="flex items-center gap-4 mb-6" data-id="element-213" data-mp="3i9detjoa9en8">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-214" data-mp="3d99284qhx42a">
                <Target size={24} data-id="element-215" data-mp="38gpieq17tcp1" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-216" data-mp="33i4qddw49zbo">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-217" data-mp="2ykmcra75mvxc">
              Emotional regulation affects how people respond to challenge,
              disagreement, uncertainty, and responsibility. Weak regulation can
              lead to rash decisions, conflict escalation, burnout, fear-driven
              thinking, and poor judgment under pressure. Strong regulation
              supports steadier decision-making, healthier relationships, better
              teamwork, and greater reliability in high-stress environments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Emotional Regulation Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-218" data-mp="9qzovcdttciv">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-219" data-mp="9tu850l3srap">
          <div className="flex items-center gap-4 mb-6" data-id="element-220" data-mp="1v4u9g95usviq">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-221" data-mp="1v5dit1hfubl5">
              <CheckCircle size={24} data-id="element-222" data-mp="2035lmqjmfi44" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-223" data-mp="2a7c61gtadzuu">
              What Strong Emotional Regulation Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-224" data-mp="256y0q6j2l40i">
            People with stronger emotional regulation can feel frustration,
            stress, anger, or fear without immediately acting on those feelings
            in destructive ways. They are better able to pause, recover,
            reframe, and respond with greater control. They do not necessarily
            feel less deeply, but they can stay functional and responsible when
            pressure rises.
          </p>
        </motion.div>
      </section>

      {/* What Weak Emotional Regulation Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-225" data-mp="2xvbycl68xybj">
        <div className="max-w-4xl mx-auto" data-id="element-226" data-mp="17laeufk8q438">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-227" data-mp="13jouya9agpjj">
            <div className="flex items-center gap-4 mb-6" data-id="element-228" data-mp="3ckrx9c2iuwa8">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-229" data-mp="3hlz51b4nzvgw">
                <XCircle size={24} data-id="element-230" data-mp="3lnt279fnihcb" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-231" data-mp="3ll6wlfcbmbwq">
                What Weak Emotional Regulation Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-232" data-mp="3vdcokp2kotbx">
              Weak emotional regulation often appears as impulsive reactions,
              rage-driven communication, panic responses, chronic overwhelm,
              inability to de-escalate, and poor stress tolerance. In personal
              life this can damage relationships and consistency. In
              institutions and leadership contexts it can create instability,
              mistrust, and repeated decision failures under pressure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-233" data-mp="1oqf1h4ji457h">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-234" data-mp="1jr23por3ddq4">
          <div className="flex items-center gap-4 mb-6" data-id="element-235" data-mp="27llif26hlx34">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-236" data-mp="22p4x0fqo9wtq">
              <Heart size={24} data-id="element-237" data-mp="304shxobxdidh" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-238" data-mp="305k2m43zhvoz">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-239" data-mp="23oegxph27faa">
            Emotional regulation can be improved through stress-management
            practices, sleep and recovery support, behavioral coaching,
            mindfulness, reflection, physical health, conflict-resolution
            training, and better awareness of triggers and patterns. In the
            SyNeural framework, it is treated as a key developmental capability
            because clear thinking becomes much harder when emotional volatility
            remains unmanaged.
          </p>
        </motion.div>
      </section>
      <CapabilityNoetivraNote capability="Emotional regulation" data-id="element-240" data-mp="2deh9jok9qr83" />
    </div>;
}